import api from 'services/api';

const normalizeText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const resolveStreetAddress = (address = {}) =>
  address.road ||
  address.pedestrian ||
  address.footway ||
  address.cycleway ||
  address.path ||
  address.residential ||
  address.highway ||
  '';

const resolveAddressLine = (address = {}, displayName = '') =>
  resolveStreetAddress(address) ||
  address.hamlet ||
  address.suburb ||
  address.neighbourhood ||
  address.city_district ||
  address.municipality ||
  String(displayName || '').split(',')[0]?.trim?.() ||
  '';

const resolveCityName = (address = {}) =>
  address.city ||
  address.town ||
  address.village ||
  address.municipality ||
  address.city_district ||
  '';

async function fetchMatchedCity({ cityName, province, countryCode }) {
  if (!cityName || !province || !countryCode) return null;

  try {
    const response = await api.get('cities', {
      params: {
        search: cityName,
        uf: province,
        country: countryCode,
      },
    });

    const cities = Array.isArray(response?.data) ? response.data : [];
    return (
      cities.find((city) => normalizeText(city?.name) === normalizeText(cityName)) ||
      cities[0] ||
      null
    );
  } catch {
    return null;
  }
}

export async function buildPointUpdatesFromReverseGeocode({
  sourcePoints = [],
  nextPoints = [],
  countryOptions = [],
  findCountryOption,
  hasCountryValue,
  defaultCountry,
}) {
  const sourceBusinessPointByKey = new Map(
    sourcePoints
      .filter(point => point?.type?.id !== 'via' && point?.action?.id !== 'via')
      .map((point, pointIndex) => {
        const pointKey = point?.identifier || point?.id || `idx-${pointIndex}`;
        return [pointKey, point];
      }),
  );

  const updates = await Promise.all(
    nextPoints
      .map((point, pointIndex) => ({
        point,
        pointIndex,
        pointKey: point?.identifier || point?.id || `idx-${pointIndex}`,
      }))
      .filter(({ point, pointKey }) => {
        if (!point) return false;
        if (point?.type?.id === 'via' || point?.action?.id === 'via') return false;

        const previousPoint = sourceBusinessPointByKey.get(pointKey);
        if (!previousPoint) return false;

        return (
          previousPoint.lat !== point.lat ||
          previousPoint.lng !== point.lng
        );
      })
      .map(async ({ point, pointIndex, pointKey }) => {
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${point.lat}&lon=${point.lng}&addressdetails=1`,
          );
          const data = await response.json();
          const addr = data.address || {};
          const cityName = resolveCityName(addr);
          const province =
            addr.state_code ||
            addr['ISO3166-2-lvl4']?.split?.('-')?.[1] ||
            addr.state ||
            point?.province ||
            '';
          const addressLine = resolveAddressLine(addr, data.display_name);
          const matchedCountry =
            findCountryOption(countryOptions, {
              abbreviation: addr.country_code,
              name: addr.country,
            }) ||
            (hasCountryValue(point?.country) ? point.country : null) ||
            defaultCountry;
          const matchedCity = await fetchMatchedCity({
            cityName,
            province,
            countryCode: addr.country_code,
          });

          return {
            pointKey,
            pointIndex,
            fields: {
              lat: point.lat,
              lng: point.lng,
              formatted_address: data.display_name || '',
              address: addressLine,
              number: addr.house_number || '',
              complement: '',
              city: matchedCity
                ? {
                    ...matchedCity,
                    name: matchedCity.name,
                  }
                : cityName
                  ? { name: cityName }
                  : point.city,
              cityId: matchedCity?.id || null,
              province:
                matchedCity?.province?.uf ||
                province,
              neighborhood: addr.suburb || addr.neighbourhood || '',
              zip_code: addr.postcode || '',
              country: matchedCountry,
              client: null,
              clientId: null,
            },
          };
        } catch {
          return {
            pointKey,
            pointIndex,
            fields: {
              lat: point.lat,
              lng: point.lng,
              complement: '',
              client: null,
              clientId: null,
            },
          };
        }
      }),
  );

  return updates.filter(Boolean);
}
